import React, { useEffect, useContext, useState, useRef } from 'react'
import { Box, CircularProgress } from '@mui/material'

import LanguageContext from "../../../contexts/LanguageContext"
import { useTexte } from "../../../contexts/FormeAdresseContext"
import UserContext, {getPointsByQuizz} from "../../../contexts/UserContext"
import { getCurrentUser } from "../../../utils/auth"
import { scoresCalculesParLeServeur } from "../../../utils/scoresServeur"


import { TopBar, BottomBar, CenterContainer, BlocIllustre, useApercuPleineHauteur, useDebordeEnBas, FonduBas, BLOC_DEFILANT, LARGEUR_LECTURE } from "./FormatsHelpers"
import { APERCU_EDITO } from "./apercu"

import EditableField from "./EditableField"
import EditableButton from "./EditableButton"
import ChargementPixelR from "./ChargementPixelR"

import EditorPopover from "../../ui/Admin/Formations/EditorPopover"
import DiagHelper from "../../ui/Admin/Formations/FormatHelpers/DiagHelper"

/* Icone de la carte de score global, node Figma 4:1445. Exportee depuis le
   fichier de Valentin et VERSEE AU DEPOT : l'URL d'export de Figma expire au
   bout de sept jours, une icone servie depuis cette URL disparaitrait donc
   toute seule. Elle etait portee au rapport du 18/09 comme « pas d'asset
   identifie » — c'etait faux, elle est simplement exportable. */
import iconeScoreGlobal from "../../../images/diagnostic-score-global.svg"
/* Fond decoratif de la carte de score, node 4:1456 : un quadrillage clair pose
   en bas a droite de la carte. Purement ornemental, donc `aria-hidden` et
   non cliquable. Verse au depot pour la meme raison que l'icone : l'URL
   d'export de Figma expire au bout de sept jours. */
import fondCarteScore from "../../../images/diagnostic-carte-fond.svg"

import './FormatDiag.css'
import { moyenneDesCategories, retenuesParLeDiagnostic, scoreDuModule } from '../../../utils/scoreGlobal'

/* Point 1 : couleur du niveau atteint sur le bilan.
 *
 * ⛔ NE PAS REVENIR A TROIS COULEURS ECRITES A LA MAIN. C'etait la premiere
 * version (vert / jaune / rouge aux tiers) ; la fiche V2 de Nawal, point 1.6,
 * la remplace explicitement : « Ne code pas trois couleurs a la main : prends
 * une echelle qui existe deja et qui gere nativement un nombre variable de
 * classes. »
 *
 * Le NOMBRE de bandes vient des seuils du contenu, que Nawal regle elle-meme
 * dans le super admin (« ≥ 66 % », « ≥ 33 % », « ≥ 0 % », plus un bouton
 * « Ajoutez un seuil »). Si elle en ajoute un quatrieme, l'echelle se
 * recalcule seule, sans qu'on retouche quoi que ce soit ici.
 *
 * L'echelle est RdYlGn de ColorBrewer, recopiee plutot qu'importee : la fiche
 * demande un modele standard « en dur », et cela evite une dependance de plus
 * pour neuf lignes de table.
 *
 * ⚠️ RdYlGn n'est pas lisible par les personnes atteintes de deficience de la
 * vision des couleurs rouge et vert, environ 8 % des hommes. La couleur ne
 * porte donc JAMAIS l'information seule : la legende sous la grille donne le
 * mot, et c'est le mot qui fait foi. Ne pas retirer la legende. */
export const RD_YL_GN = {
  3:  ["#FC8D59", "#FFFFBF", "#91CF60"],
  4:  ["#D7191C", "#FDAE61", "#A6D96A", "#1A9641"],
  5:  ["#D7191C", "#FDAE61", "#FFFFBF", "#A6D96A", "#1A9641"],
  6:  ["#D73027", "#FC8D59", "#FEE08B", "#D9EF8B", "#91CF60", "#1A9850"],
  7:  ["#D73027", "#FC8D59", "#FEE08B", "#FFFFBF", "#D9EF8B", "#91CF60", "#1A9850"],
  8:  ["#D73027", "#F46D43", "#FDAE61", "#FEE08B", "#D9EF8B", "#A6D96A", "#66BD63", "#1A9850"],
  9:  ["#D73027", "#F46D43", "#FDAE61", "#FEE08B", "#FFFFBF", "#D9EF8B", "#A6D96A", "#66BD63", "#1A9850"],
  10: ["#A50026", "#D73027", "#F46D43", "#FDAE61", "#FEE08B", "#D9EF8B", "#A6D96A", "#66BD63", "#1A9850", "#006837"],
  11: ["#A50026", "#D73027", "#F46D43", "#FDAE61", "#FEE08B", "#FFFFBF", "#D9EF8B", "#A6D96A", "#66BD63", "#1A9850", "#006837"],
}

/* `iPalier` est l'index du seuil atteint, et les seuils sont ranges du PLUS
   HAUT au plus bas : l'index 0 est donc le meilleur niveau, quand l'echelle,
   elle, va du rouge au vert. La correspondance s'inverse. */
/* MEDIANE DE COHORTE — node Figma 4:1458 (carte de score) et legende 14px sur
   chaque ligne de competence.
   Le back calcule et accroche `format.medianes`, indexe par cle exactement
   comme `userChoice` : { master: {...}, "Ecoute active": {...} }. Le front ne
   fait AUCUNE statistique et ne recoit AUCUN score nominatif : seulement une
   valeur, un effectif et un seuil.

   ⛔ SOUS LE SEUIL, ON N'AFFICHE RIEN. Pas une valeur approchee, pas une
   phrase de remplacement : la formulation en dessous du seuil n'est pas
   arbitree par Nawal, et inventer un texte serait une decision produit prise
   en passant. `suffisant` est calcule par le back, le front ne le recalcule
   pas et ne le contourne pas. */
export const medianeAffichable = (medianes, cle) => {
  const m = medianes && cle != null ? medianes[cle] : null
  if (!m || m.suffisant !== true) return null
  return typeof m.medianeArrondie === 'number' ? m.medianeArrondie : null
}

/* (25/09/2026) Un pourcentage ecrit selon la langue de l'ecran. En francais,
   une espace INSECABLE avant « % » : une espace ordinaire laisserait « % »
   partir seul a la ligne. En anglais, aucune espace (« 54% ») : l'espace
   devant « % » est une regle francaise, pas une regle generale. Langue
   inconnue ou absente : francais, la langue par defaut du site. */
/* ⛔ 06/10/2026 — REVIEW DE NAWAL DU 5 OCT, POINT 1.5 : ESPACE FINE INSECABLE
   (U+202F), « 50 % », sur TOUS les pourcentages de l'ecran Diagnostic : carte du
   score global, lignes de competences, mediane, cle des couleurs. Le score
   global et les lignes collaient encore le « % » au nombre (« 50% »).
   L'anglais reste sans espace (regle ci-dessus). */
const separateurPourcent = (langue) => (langue === 'en' ? '' : '\u202f')
export const pourcentLisible = (n, langue) => n + separateurPourcent(langue) + '%'

/* Le libelle complet, ou null s'il n'y a rien a dire. Le libelle vient du
   dictionnaire ; sans lui, on n'affiche pas un nombre nu, qui ne voudrait
   rien dire a l'apprenant. */
export const texteMediane = (medianes, cle, libelle, langue) => {
  const v = medianeAffichable(medianes, cle)
  if (v === null || !libelle) return null
  return libelle + ' ' + pourcentLisible(v, langue)
}

/* ⛔ 06/10/2026 — REVIEW DE NAWAL DU 5 OCT, POINT 4.4 b : LA MISE EN SCENE DU
   BILAN, EN UN SEUL ENDROIT. Le voile « Analyse de tes reponses » s'efface de
   2 s a 3,5 s ; la carte de la competence i (0, 1, ...) apparait de 2,5 x (i+1)
   s en 2 s. « Suivant » s'active a la fin de la DERNIERE chose qui s'anime :
   la derniere carte de la liste, ou le voile s'il n'y a pas de liste (une
   seule competence, point 1.2). Le score global n'a pas d'animation a lui.
   Avant, l'attente valait 2,5 s par entree de `format.categorie`, `master` et
   `glbMaster` compris : sans eux, « Suivant » s'activait au DEBUT de
   l'apparition de la derniere carte ; avec eux, 0,5 a 3 s apres sa fin. */
export const MISE_EN_SCENE_BILAN = { delaiCarte: 2.5, dureeCarte: 2, debutVoile: 2, dureeVoile: 1.5 }
export const attenteAvantSuivant = (nbCartesAnimees) => {
  const m = MISE_EN_SCENE_BILAN
  const finVoile = m.debutVoile + m.dureeVoile
  const finDerniereCarte = nbCartesAnimees > 0 ? nbCartesAnimees * m.delaiCarte + m.dureeCarte : 0
  return Math.max(finVoile, finDerniereCarte) * 1000
}

export const couleurDuPalier = (iPalier, nPaliers) => {
  const n = Math.min(Math.max(Number(nPaliers) || 0, 3), 11)
  const echelle = RD_YL_GN[n]
  const i = Number(iPalier)
  if (!echelle || !Number.isFinite(i)) return "#D4D4D4"
  const rang = echelle.length - 1 - Math.min(Math.max(i, 0), echelle.length - 1)
  return echelle[rang]
}

/* ⛔ 30/09/2026 — FICHE DE RETOUR DE NAWAL, POINT 10 (et reponse a sa question du
   point 83). Les deux titres de la page de resultats du Diagnostic etaient ecrits
   en dur dans le dictionnaire : « impossible de les modifier depuis l'editeur ».
   Meme grief, mot pour mot, que le point 1.1 de la fiche V2 sur le feedback du quiz.

   OU ILS VIVENT : dans `answers`, colonne JSON deja persistee et deja autorisee a
   l'edition cote serveur (`utils/colonnesAutorisees.js`). AUCUNE colonne nouvelle,
   AUCUNE MIGRATION — c'est la regle 2 de `FORMATS-THE-LAB.md`, celle qui porte
   deja `answers.quizVariant` et `answers.type`.

   LES TROIS ETATS SONT DISTINCTS, et c'est ce que la fiche demande :
     cle absente   -> la valeur par defaut (le dictionnaire), comme aujourd'hui.
                      C'est ce qui permet de ne toucher AUCUN contenu existant ;
     chaine vide   -> le titre ne s'affiche pas (« champ vide : le texte ne
                      s'affiche pas ») ;
     chaine pleine -> elle s'affiche telle quelle.

   ⚠️ UNE SEULE REGLE, ICI : le lecteur (`FormatDiag`) et l'editeur (`DiagHelper`)
   l'appellent tous les deux. Deux lectures separees auraient fini par diverger, et
   l'editeur aurait montre autre chose que l'ecran de l'apprenant.
   ⚠️ Le garde `typeof` protege d'une colonne doublement encodee : `answers` serait
   alors une CHAINE, et `answers[cle]` rendrait un caractere au lieu d'un texte.
   Meme precaution que QuizzHelper sur la fusion de `answers`. */
export const titreDeLaPage = (answers, cle, defaut) => {
  const a = (answers && typeof answers === "object") ? answers : {}
  const v = a[cle]
  return typeof v === "string" ? v : (defaut || "")
}

const FormatDiag = ({ actFormation, actIndex, format, setViewPlanCours, viewPlanCours, onlyContent, isEditor, sequenceSubMenu, setSequenceSubMenu }) => {

  const { actLanguage, languageValues } = useContext(LanguageContext)
  /* Tutoiement / vouvoiement selon la cible du module (07/10/2026,
     contexts/FormeAdresseContext.js). */
  const t = useTexte()

  const { manageFormatResponse, manageSlide } = useContext(UserContext)

  /* PD-10 (25/09/2026, decision d'Hicham) : un candidat THE SCAN ne voit AUCUNE
     statistique de ses resultats — ni score global, ni competences, ni
     positionnement, ni paliers, ni mediane, ni meme le voile « Analyse de tes
     reponses », qui annonce un resultat. Il voit un ecran neutre et passe.
     ⚠️ LE CALCUL ET L'ENREGISTREMENT NE CHANGENT PAS : `getUserResult` tourne
     comme avant et le bouton envoie les MEMES donnees (isDone + userChoice =
     resultats), car le recruteur en a besoin. Seul l'affichage est coupe.
     Hors editeur et hors vignette (`onlyContent`) seulement : l'admin garde son
     rendu, et un candidat n'a de toute facon ni plan du module (PD-4) ni
     vignette de fin (PD-5). */
  const vueCandidat = getCurrentUser()?.type === "theScanCandidat" && isEditor !== true && onlyContent !== true
  /* 27/09/2026, chantier « scores cote serveur » : quand le serveur note ce
     candidat (module arrive sans glbMasterPond), il n'envoie plus les points
     des questions. `getUserResult` lirait des points absents et produirait un
     bilan faux, que le serveur ignorerait de toute facon (il ne garde pas le
     userChoice d'un Diagnostic de candidat). On ne calcule donc rien, on
     n'envoie que isDone, et « Suivant » est actif tout de suite : il n'y a
     plus de resultats a attendre. Avec l'ancien serveur (cle glbMasterPond
     presente), le paragraphe PD-10 ci-dessus reste vrai tel quel. */
  const bilanParLeServeur = vueCandidat && scoresCalculesParLeServeur(actFormation)

  const [isAlreadyAnswer, setIsAlreadyAnswer] = useState(false)
  const [results, setResults] = useState(null)
  /* (25/09/2026) Un Diagnostic pas encore fait ne doit JAMAIS partir sans ses
     resultats. Ils sont calcules par un effet, donc APRES le premier rendu : un
     clic dans cet intervalle enverrait isDone: true avec userChoice: null,
     l'ecran passerait a « fait » et le recruteur perdrait le Diagnostic, sans
     que personne ne le voie. Un ecran deja fait n'envoie plus rien (le
     contexte ignore un ecran fait) : il n'a pas a attendre. */
  const resultatsPasPrets = !bilanParLeServeur && results === null && format?.isDone !== true
  useEffect(() => {
    if (format?.isDone) {
      setIsAlreadyAnswer(true)
      if (format?.userChoice) {
        setResults(format?.userChoice)
      } else {
        setResults(null)
      }
    } else {
      if (!onlyContent && !bilanParLeServeur) {
        getUserResult()
      }
      setIsAlreadyAnswer(false)
    }
  }, [format, onlyContent])

  const getUserResult = () => {
    //console.log("getUserResult")
    let categories = {master: { points: 0, max: 0, score: 0 }}
    if (format?.isChapter === true && actFormation) {
      categories.glbMaster = { points: 0, max: 0, score: 0 }
    }

    let glbMasterPond = []
    /* 27/09/2026 : meme garde que UserContext.getFormationResults. Un module
       sans glbMasterPond (nouveau serveur, candidat THE SCAN) faisait lever
       JSON.parse(undefined). */
    if (actFormation?.glbMasterPond !== null && actFormation?.glbMasterPond !== undefined) {
      glbMasterPond = JSON.parse(actFormation?.glbMasterPond)
    } else if (actFormation && actFormation.glbMasterPond === undefined) {
      // 27/09/2026 : meme trace que UserContext.getFormationResults.
      console.warn("Diagnostic : module " + actFormation.id + " sans glbMasterPond, calcul sans ponderation")
    }

    const manageFormat = (format) => {
      if (['Quizz', 'QuestionnaireV2'].includes(format.type )) {
        const [points, max] = getPointsByQuizz(format)
        //const makeResult = (o) => ({ ...o, max: o.max + max, points: o.points + points, score: o.max > 0 ? Math.round((o.points / o.max) * 100) : 100 })
        const makeResult = (c, points, max) => {
          let newPoints = c.points+points
          let newMax = c.max+max
          return {
            points : newPoints,
            max : newMax,
            score : Math.round((newPoints / newMax) * 100)
          }
        }
        categories.master = makeResult(categories.master, points, max)
        if (format.categorie && format.categorie !== 'master') {
          categories[format.categorie] = makeResult(categories[format.categorie] ?? { points: 0, max: 0, score: 0  }, points, max)
        }
      }
    }

    /* points par categorie sur TOUT le module, pour le score du module
       (glbMaster) : moyenne des categories, regle du 07/10/2026 */
    const categoriesDuModule = { master: { points: 0, max: 0 } }   // master : le total, pour le repli 2
    const manageFormatGlb = (format) => {
      if (['Quizz', 'QuestionnaireV2'].includes(format.type )) {
        const [points, max] = getPointsByQuizz(format)
        categoriesDuModule.master = { points: categoriesDuModule.master.points + points, max: categoriesDuModule.master.max + max }
        if (format.categorie && format.categorie !== 'master') {
          const c = categoriesDuModule[format.categorie] ?? { points: 0, max: 0 }
          categoriesDuModule[format.categorie] = { points: c.points + points, max: c.max + max }
        }
        //const makeResult = (o) => ({ ...o, max: o.max + max, points: o.points + points, score: o.max > 0 ? Math.round((o.points / o.max) * 100) : 100 })
        const makeResult = (c, points, max) => {
          //on cherche si la categorie compte : selection manuelle, jamais par defaut (07/10/2026)
          let statut = "non"
          let find = glbMasterPond.find((el) => el.key === format.categorie)
          if (find) {
            statut = find.value
          }
          if (statut === "oui") {
            let newPoints = c.points+points
            let newMax = c.max+max
            return {
              points : newPoints,
              max : newMax,
              score : Math.round((newPoints / newMax) * 100)
            }
          } else {
            return {
              points : c.points,
              max : c.max,
              score : Math.round((c.points / c.max) * 100),
            }
          }
        }
        categories.glbMaster = makeResult(categories.glbMaster, points, max)
      }
    }

    //formation
    if (format?.isChapter === true) {
      //master glb
      if (actFormation) {
        for (var c in actFormation?.chapitre) {
          for (var i in actFormation?.chapitre[c]?.partie) {
            for (var n in actFormation?.chapitre[c]?.partie[i]?.sequence) {
              manageFormatGlb(actFormation?.chapitre[c]?.partie[i]?.sequence[n])
            }
          }
        }
      }
      //master + autres catégories
      const parts = actFormation?.chapitre[actIndex?.chapitre]?.partie
      for (var i in parts) {
        for (var n in parts[i].sequence) {
          manageFormat(parts[i].sequence[n])
        }
      }
    } else {
      let actPart = actFormation?.chapitre[actIndex?.chapitre]?.partie[actIndex?.partie]
      for (var i = actIndex?.sequence - 1; i >= 0; i--) {
        manageFormat(actPart?.sequence[i])
      }
    }

    /* REGLE DU 07/10/2026 (Nawal, point 3.3) : score global = moyenne simple
       des categories cochees dans cette slide (toutes si aucune), et non plus
       le total des points ; score du module = moyenne des categories « oui »
       de la ponderation (utils/scoreGlobal.js). */
    const cochees = (Array.isArray(format?.categorie) ? format.categorie : []).map((c) => c?.key)
    categories.master.score = moyenneDesCategories(categories, retenuesParLeDiagnostic(cochees))
    if (categories.glbMaster) {
      categories.glbMaster.score = scoreDuModule(categoriesDuModule, glbMasterPond)
    }

    setResults(categories)
  }

  /* ⛔ 06/10/2026 — REVIEW DE NAWAL DU 5 OCT, POINT 3.1 : DANS LA SLIDE, EN
     EDITEUR, LE TEXTE D'UN PALIER S'ECRIT SUR PLACE, MEME VIDE. Un champ vide
     n'etait pas rendu (texte de categorie, `{subTitle && ...}`) ou ne mesurait
     rien (texte global) : Nawal ne pouvait pas cliquer dedans. En editeur, le
     champ est toujours la, avec un texte indicatif (« Texte affiché si le score
     est ≥ 33 % ») tant qu'il est vide ; on change de palier avec l'oeil du
     parametrage. Cote apprenant, rien ne change : pas de champ, pas de fond, et
     un texte vide ne laisse aucune place.
     Ecriture : le chemin d'avant, `EditableField` puis `onSequenceChange`
     (`total[i].text`, `categorie[].seuils[j].text`). */
  const champDePalierVide = (seuil) => (isEditor ? {
    minHeight:"1em",
    "&:empty::before": {
      content: JSON.stringify((languageValues?.formation?.diagTexteSiScore?.[actLanguage] || "")
        .replace("{seuil}", pourcentLisible(seuil ?? 0, actLanguage))),
      color:"rgba(10,10,11,0.35)",
    },
  } : {})

  const libelleMediane = (cle) =>
    texteMediane(format?.medianes, cle, languageValues?.formation?.medianeCohorte?.[actLanguage], actLanguage)

  const [catsToDisplay, setCatsToDisplay] = useState([])
  const [activeButton, setActiveButton] = useState(false)

  useEffect(() => {
    if (results && format) {
      //pour categories
      let categorieByScore = JSON.parse(JSON.stringify(format?.categorie))
      categorieByScore.map((cat, i) => {
        let userResult = results[cat.key] ? results[cat.key] : {score: 0, points: 0, max : 0}
        cat.score = userResult?.score
      })

      //on sort les catégories master et glbMaster du trie
      let masterCats = []
      let findMaster = categorieByScore.find((el) => el.key === "master")
      if (findMaster) {
        masterCats.push(findMaster)
      }
      let findGlbMaster = categorieByScore.find((el) => el.key === "glbMaster")
      if (findGlbMaster) {
        masterCats.push(findGlbMaster)
      }

      //on supprimer les catégories master et glbMaster du premier tableau
      categorieByScore = categorieByScore.filter((el) => el.key !== "master")
      categorieByScore = categorieByScore.filter((el) => el.key !== "glbMaster")

      if (isEditor !== true) {
        categorieByScore.sort((a,b) => b.score - a.score);
      } else {
        //console.log("on est sur l'éditeur -> pas de besoin de trier par score")
      }

      categorieByScore = masterCats.concat(categorieByScore)
      setCatsToDisplay(categorieByScore)

      if (isEditor !== true) {
        /* Point 4.4 b : les seules cartes animees sont les competences de la
           liste, et seulement quand la liste s'affiche (deux ou plus, point 1.2) ;
           ni `master` ni `glbMaster`. AFFICHAGE SEUL : rien de ce qui part au
           serveur ne change. */
        const nbCompetences = categorieByScore.filter((c) => c?.key !== "master" && c?.key !== "glbMaster").length
        let timeForButton = attenteAvantSuivant(nbCompetences >= 2 ? nbCompetences : 0)
        setTimeout(() => {
          setActiveButton(true)
        }, timeForButton);
      }



    } else {
      setCatsToDisplay([])
    }
  }, [results, format, isEditor])

  /* ⛔ 01/10/2026 — « VOS 1 COMPETENCES » SUIVI DE RIEN. Constat d'Hicham sur
     LAB, capture du Diagnostic de fin de chapitre.
     `catsToDisplay` contient `master` et `glbMaster` EN TETE (ligne 328, ils y
     sont remis expres : la carte de score global les lit). Mais la liste, elle,
     les RETIRE au rendu. Trois endroits lisaient donc la mauvaise chose :
       - le compteur « Vos {n} competences » comptait les master. Avec une seule
         vraie competence et un master, il annoncait 2 ; avec AUCUNE vraie
         competence et un master, il annoncait « Vos 1 competences » et
         n'affichait rien du tout. C'est la capture d'Hicham ;
       - la cle de lecture des couleurs lisait `catsToDisplay[0].seuils`, donc
         les seuils du MASTER et non ceux d'une competence — des chiffres qui
         viennent d'ailleurs, ce qu'Hicham a decrit comme « ceux de la slide
         d'apres » ;
       - le titre lui-meme s'affichait alors qu'il n'annoncait aucune liste.
     Une seule liste desormais, calculee ici, et les trois la lisent. */
  const competencesAffichees = (catsToDisplay || [])
    .filter((c) => c?.key !== "master" && c?.key !== "glbMaster")
  /* ⛔ 06/10/2026 — REVIEW DE NAWAL DU 5 OCT, POINT 1.2 : UNE SEULE CATEGORIE,
     LE SCORE GLOBAL SEUL. Avec une seule competence cochee, l'ecran montrait le
     score global puis le meme score une seconde fois, en ligne de competence.
     En dessous de deux, tout ce qui concerne les categories est masque : filet,
     titre « Vos n competences », lignes, fondu, cle des couleurs. A partir de
     deux, rien ne change. AFFICHAGE SEUL : le calcul et ce qui part au serveur
     (`userChoice` = resultats complets) ne changent pas, ni l'attente du bouton.
     Le voile « Analyse de tes reponses » reste rendu (il vit dans la liste). */
  const afficherCategories = competencesAffichees.length >= 2

  /* 01/10/2026 (PD-13, tranche par Hicham) : la liste des competences DEFILE
     quand elle deborde, et le fondu ne se montre que dans ce cas. */
  const [refListe, listeDeborde] = useDebordeEnBas()
  const titreEditable = (cle, defaut) => titreDeLaPage(format?.answers, cle, defaut)

  const getTitleText = () => {
    if (results && format) {
      let masterScore = results?.master?.score ? results?.master?.score : 0
      for (let i=0; i<format?.total?.length; i++) {
        let seuil = format?.total[i]
        if (masterScore >= seuil?.seuil) {
          return seuil?.text
          break;
        }
      }
    }

    return ""
  }

  /* EDITOR ADMIN */
  useEffect(() => {
    if (isEditor) {
      let fakeResults = {}
      let categorieByScore = JSON.parse(JSON.stringify(format?.categorie))
      categorieByScore.map((cat, i) => {
        let findInActResults = results ? results[cat?.key] : null
        if (findInActResults) {
          fakeResults[cat.key] = findInActResults
        } else {
          fakeResults[cat.key] = {score : 0}
        }
      })
      /* ⛔ 06/10/2026 — REVIEW DE NAWAL DU 5 OCT, POINT 4.2 : « master » N'EST PAS
         UNE CATEGORIE, C'EST LE SCORE GLOBAL. Construit sur les seules
         categories cochees, l'apercu perdait `master` quand il etait decoche :
         la carte du score global disparaissait de la slide, et le texte global
         (`getTitleText`, `stepIndex`) lisait un score absent. Il est toujours la,
         avec la meme valeur d'apercu que les autres (celle d'avant, sinon 0).
         Cote apprenant, rien ne change : `getUserResult` pose toujours `master`. */
      fakeResults.master = (results && results.master) ? results.master : {score : 0}

      setResults(fakeResults)
    }
  }, [format, isEditor])

  const [stepIndex, setStepIndex] = useState(0)
  const [categoriesIndexes, setCategoriesIndexes] = useState({})
  useEffect(() => {
    if (isEditor) {
      if (results) {
        //step Index
        var iSeuil = 0;
        var maxSeuil = 0;
        let thisScore = results["master"]?.score
        let thisSeuils = format?.total
        for (var i =0; i < thisSeuils?.length; i++ ) {
          const step = thisSeuils[i]
          if (thisScore >= step.seuil && step.seuil >= maxSeuil) {
            maxSeuil = step.seuil
            iSeuil = i
          }
        }
        setStepIndex(iSeuil)

        //categoriesIndexes
        const categoriesIndex={}
        for (var cat of format?.categorie) {
          var iSeuil = 0;
          var maxSeuil = 0;
          let thisScore = results[cat?.key]?.score
          let thisSeuils = cat?.seuils
          for (var i =0; i < thisSeuils?.length; i++ ) {
            const step = thisSeuils[i]
            if (thisScore >= step.seuil && step.seuil >= maxSeuil) {
              maxSeuil = step.seuil
              iSeuil = i
            }
          }
          categoriesIndex[cat?.key] = iSeuil
        }
        setCategoriesIndexes(categoriesIndex)

      }
    }
  }, [results, format, isEditor])
  /* END EDITOR ADMIN */


  /* ⛔ MEME REGLE QUE LES AUTRES ECRANS (09/09/2026). Le diagnostic n'a jamais
     porte d'image : son contenu restait centre quand celui des ecrans voisins
     se ferre a gauche. L'ancien lecteur lui donnait deja `MaskQuizz`, le meme
     panneau que le quiz — le radar et les paliers restent centres DANS leur
     colonne, mais la colonne se pose au meme endroit que partout ailleurs. */
  const pleineHauteur = useApercuPleineHauteur()
  /* Le cote de l'image alterne d'un ecran a l'autre, regulierement. */
  /* Ce format ne porte jamais d'image : ancrage a gauche, sans alternance. */
  const cote = pleineHauteur ? "droite" : null

  return (
    <Box sx={{
      display:"flex",
      flexDirection:"column",
      /* ⚠️ `100vh` vaut la hauteur de la FENETRE, jamais celle d'un conteneur.
         Un apercu qui simule 982 px dans un panneau aurait donc affiche un ecran
         calibre sur la vraie fenetre. La variable `--hs-hauteur-ecran` n'est
         definie QUE par le panneau d'apercu ; partout ailleurs le repli `100vh`
         s'applique et rien ne change. Une variable CSS plutot qu'un prop : neuf
         signatures de composants restent intactes, et un format rendu hors du
         lecteur se comporte correctement sans qu'on ait pense a le lui dire. */
      height:onlyContent?"100%":"var(--hs-hauteur-ecran, 100vh)",
      /* ⛔ LE FORMAT DEVIENT SON PROPRE REFERENTIEL DE MESURE (08/09/2026).
         Nawal voit « de grosses differences » entre l'apercu de l'admin et la
         vue apprenant. Cause : l'apercu ne rend pas l'ecran a la taille de la
         fenetre. `ScaleThumbForm` le rend dans une boite FIXE de 1500 x 1000,
         puis la reduit par un `transform: scale()`. Or `vw` et `vh` se
         calculent sur la FENETRE, jamais sur cette boite : les proportions
         divergeaient donc des deux cotes, sans qu'aucun reglage puisse les
         reconcilier — ce sont deux referentiels differents.
         En declarant ce conteneur, les unites `cqw`/`cqh` employees plus bas se
         mesurent sur LUI. Dans le lecteur il occupe la fenetre : le rendu ne
         bouge pas d'un pixel. Dans l'apercu il fait 1500 x 1000 : le rendu
         devient fidele. */
      containerType:"size",
      /* L'ancre du panneau : sans elle le bloc absolu se cale sur la fenetre. */
      position: APERCU_EDITO ? "relative" : "static",
      overflow:"hidden",
      /* ⛔ Pas de gap racine : la planche colle le contenu au bandeau. */
    }}>
      { /* EDITOR ADMIN */}
      {isEditor &&
        <EditorPopover
          open={sequenceSubMenu}
        >
          <DiagHelper
            format={format}
            results={results}
            setResults={setResults}
            stepIndex={stepIndex}
            categoriesIndexes={categoriesIndexes}
            actFormation={actFormation}
            actIndex={actIndex}
          />
        </EditorPopover>
      }
      <TopBar
        actFormation={actFormation}
        actIndex={actIndex}
        setViewPlanCours={setViewPlanCours}
        viewPlanCours={viewPlanCours}
        onlyContent={onlyContent}
      />
      {/* 04/10/2026 — PLANCHE 14 : la pastille en tete de la colonne de gauche
          (`pastilleEnTete`), et le haut de la planche : 72 px sous l'en-tete en
          Desktop (4:1435 a y=147), 50 en Tablet (4:1609 a y=125) et en Mobile
          (4:1768 a y=168, barre d'etat de 59 et en-tete de 59 deduits, moins
          les 2 px de la piste que la planche Mobile ne dessine pas). Palier md
          (1130-1240) : FIGMA MANQUANT, 72 deduit du Desktop. */}
      <CenterContainer
        actFormation={actFormation}
        actIndex={actIndex}
        onlyContent={onlyContent}
        cote={cote}
        pastilleDansColonne
        pastilleEnTete={["20px", "30px", "30px", "30px"]}
        retraitHaut={[48, 50, 72, 72]}
      >
        {({ pastille }) => (
        <BlocIllustre
          image={null}
          horsFlux={cote}
          sx={{ flex:1, minHeight:0 }}
        >
        {/* PD-10 (25/09/2026) : l'ecran neutre du candidat THE SCAN remplace
            TOUT le bilan ci-dessous (voir `vueCandidat`). En-tete et bouton
            restent ceux de tous les ecrans. */}
        {/* ⛔ pastille et texte dans UNE colonne : poses separement, ils
            devenaient deux voisins de la rangee de `BlocIllustre` aux grands
            paliers — la pastille a GAUCHE du texte (relecture du 04/10, le
            defaut de B85) */}
        {vueCandidat &&
          <Box sx={{ display:"flex", flexDirection:"column", alignItems:"flex-start", width:"100%", maxWidth: LARGEUR_LECTURE }}>
          {pastille}
          <Box sx={{
            width:"100%",
            maxWidth: LARGEUR_LECTURE,
            color:"text.black",
            fontSize:["20px", "24px", "26px", "28px"],
            lineHeight:1.3,
            textAlign:"left",
          }}>
            {t(languageValues?.formation?.merciPourVosReponses)}
          </Box>
          </Box>
        }
        {/* sans resultats (calcul en cours, ecran fait sans reponses) le bilan
            est masque : la pastille reste, comme avant le 04/10 */}
        {!vueCandidat && !(isEditor || results) && pastille}
        {!vueCandidat &&
        <Box sx={{
          width:"100%",
          /* ⛔ La hauteur ne peut pas etre un nombre magique. `calc(100vh - 260px)`
             supposait un bandeau haut et bas d'une hauteur fixe : le bloc
             descendait donc SOUS la barre du bas, et la derniere proposition du
             quiz s'y trouvait tranchee. Sans barre de defilement visible (L10,
             demande de Nawal), rien n'indiquait a l'apprenant qu'il restait une
             reponse : il pouvait repondre sans l'avoir lue.
             `flex: 1` avec `minHeight: 0` prend exactement la place que le
             conteneur central laisse, quelle que soit la hauteur des bandeaux.
             `minHeight: 0` est indispensable : sans lui, un enfant en overflow
             refuse de retrecir dans un parent flex. */
          flex:["unset", "unset", "unset", 1],
          minHeight:0,
          /* ⚠️ APRES `width`, jamais avant : un spread pose plus haut serait
             ecrase par la propriete qui le suit. */
          /* Ce format n'a pas d'image : largeur de lecture, bord gauche commun. */
          ...(pleineHauteur ? { width:["100%", "100%", "min(52cqw, 720px)", "min(52cqw, 720px)"],
                                maxWidth:"100%", position:"relative", zIndex:1 } : {}),
          display:isEditor? "flex" : results?"flex":"none",
          flexDirection:["column", "column", "column", "row"],
          gap:["20px", "40px", "40px", "60px"],
          /* Le debordement n'est pas retenu ici : c'est la zone de contenu
             au-dessus qui defile, pour toute la page d'un bloc.
             Planche desktop 4:1414, 1512 x 982 : bandeau 75 + ecart 72 + bloc
             du haut 270 + conteneur de liste 4:1459 (643) + barre du bas 80
             = 1140. La planche depasse son propre cadre de 158 px : elle decrit
             une page qui defile, et non une liste dans une lucarne. */
          overflow:"unset",
        }}>
          <Box sx={{
            flex:["unset", "unset", "unset", 1],
            minWidth:0,
            display:"flex",
            flexDirection:"column",
          }}>
            {/* Point 6, en local : ce bloc se centrait verticalement lui aussi
                (`margin: auto 0`), ce qui deplacait le titre du bilan selon le
                nombre de competences affichees. Ancre en haut, comme partout. */}
            <Box sx={{
              /* ⚠️ LARGEUR DE LA COLONNE DE CONTENU : 1320, node 4:1432.
                 96 + 1320 + 96 = 1512 a l'unite. Elle porte le bloc du haut ET
                 la liste : les deux partagent donc le meme bord, et la carte de
                 score se cale sur 1416 comme sur la planche. */
              width:"100%",
              /* 750 en Tablet (4:1606 a x=42 : 834 - 2 x 42) : le conteneur
                 central y laisse 754 */
              maxWidth:["100%", "750px", "1320px", "1320px"],
              marginLeft:"auto",
              marginRight:"auto",
              flex:1,
              minHeight:0,
              display:"flex",
              flexDirection:"column",
            }}>
              {/* ⛔ EN-TETE EN DEUX COLONNES, D'APRES LE NODE 4:1433.
                  La planche pose une rangee `justify-between` : a gauche une
                  colonne de 650 px — pastille, titre 42px, texte d'introduction
                  20px — et a DROITE la carte de score, large de 320. Le code les
                  empilait, titre pleine largeur puis carte en dessous a gauche.
                  Capture LAB : titre sur six lignes occupant tout l'ecran et une
                  boite de score isolee sous lui. */}
              <Box sx={{
                display:"flex",
                width:"100%",
                flexDirection:["column", "column", "column", "row"],
                /* 04/10/2026 : tout est ferre a gauche, aux trois planches
                   (4:1434, 4:1608, 4:1767 a x=96, 42, 22) ; la carte de score se
                   cale en bas a droite en Desktop (4:1433 : `MAX`). */
                alignItems:["stretch", "flex-start", "flex-start", "flex-end"],
                justifyContent:"space-between",
                /* 40 entre la colonne et la carte quand elles s'empilent
                   (4:1607, 4:1766) */
                gap:"40px",
                flexShrink:0,
              }}>
              {/* ⛔ 04/10/2026 — LA COLONNE DE GAUCHE MANQUAIT. Le node 4:1434 est
                  une colonne de 650 px : pastille, titre, introduction, l'une
                  sous l'autre, a 30 px d'ecart (20 en Mobile, 4:1767). Les trois
                  etaient poses DIRECTEMENT dans la rangee : en Desktop, titre,
                  introduction et carte devenaient trois colonnes (titre a 96
                  large de 279 sur quatre lignes, introduction a 415, photos du
                  banc). */}
              <Box sx={{
                display:"flex",
                flexDirection:"column",
                alignItems:"flex-start",
                width:"100%",
                maxWidth:["100%", "650px", "650px", "650px"],
                minWidth:0,
                /* EN HAUT, la carte seule se cale en bas : une introduction
                   courte faisait descendre la pastille et le titre (point 6 de
                   Nawal, relecture du 04/10 : 147 → 169 avec « Bravo. ») */
                alignSelf:"flex-start",
              }}>
              {pastille}
              {/* ⛔ HIERARCHIE DU DIAGNOSTIC (21/09/2026, lot C). La planche 14 porte un
                  TITRE COURT ET CONSTANT, puis le texte de restitution EN CORPS DE TEXTE.
                  Le produit n'avait pas ce titre : le feedback occupait le slot de titre et
                  etait rendu a 42 px. Sur un module reel, cela donnait un pave de six lignes
                  qui repoussait la carte de score et la liste de competences vers le bas.
                  Le defaut etait visible des la capture de cloture du 18/09, a 1512 : ce
                  n'est ni le contenu historique ni la largeur qui le causaient. */}
              {/* Point 10 : le titre vient de `answers.titrePositionnement` s'il a
                  ete saisi, du dictionnaire sinon, et disparait si le champ a ete
                  vide volontairement (voir `titreEditable`). */}
              {!!titreEditable("titrePositionnement", t(languageValues?.formation?.votrePositionnement)) &&
                <Box sx={{
                  /* noir pur, 42 / 48,3 (30 / 34,5 en Mobile) : 4:1439, 4:1772 */
                  color:"text.black",
                  fontSize:["30px", "42px", "42px", "42px"],   /* titre de la planche */
                  textAlign:"left",
                  lineHeight:1.15,
                  /* Rogne aux capitales et a la ligne de base, comme la planche :
                     ses ecarts de 30 px (20 en Mobile) se comptent entre textes
                     rognes (4:1434, 4:1767 ; meme regle que FormatTitre). */
                  textBox:"trim-both cap alphabetic",
                  marginBottom:["20px", "30px", "30px", "30px"],
                }}>
                  {titreEditable("titrePositionnement", t(languageValues?.formation?.votrePositionnement))}
                </Box>
              }
              <EditableField
                /* Point 3.1 : en editeur, le texte du palier EDITE (`stepIndex`,
                   celui que l'oeil designe), pour que la frappe aille au bon
                   endroit meme si les seuils ne sont pas encore tries */
                field={isEditor ? (format?.total?.[stepIndex]?.text || "") : getTitleText()}
                isEditor={isEditor}
                sx={{
                  /* 20 / 30 en noir a 70 % (15 / 22,5 en Mobile) : 4:1440, 4:1773.
                     Le code posait 18 px noir pur, interligne 1,15. */
                  color:"text.subTitle",
                  fontSize:["15px", "20px", "20px", "20px"],  /* corps de texte : la planche reserve les 42 px au titre fixe */
                  textAlign:"left",
                  lineHeight:1.5,
                  textBox:"trim-both cap alphabetic",
                  ...champDePalierVide(format?.total?.[stepIndex]?.seuil),
                }}
                type={["total", stepIndex, "text"]}
              />
              </Box>
              {/* CARTE SCORE GLOBAL — node 4:1442 : carte blanche bordee
                  `#e7e7e7`, large de 320, avec le libelle en 18px et le score
                  en 62px. */}
              {results?.master &&
                <Box sx={{
                  flexShrink:0,
                  backgroundColor:"white",
                  /* FIGMA EXACT. Tablet `4:1615` pose la carte a x=0, donc a
                     GAUCHE sous le bloc de titre, large de 320 — pas centree.
                     Mobile `4:1774` la fait pleine largeur (350 sur 393).
                     ⚠️ 834 px tombe dans le palier `sm`, pas `md` : les bornes
                     sont [0, 600, 1130, 1240]. C'est la 2e case qui decrit la
                     tablette, pas la 3e. */
                  alignSelf:["stretch", "flex-start", "flex-start", "flex-end"],
                  marginTop:0,
                  border:"1px solid #E7E7E7",
                  /* ⛔ AUCUNE OMBRE dans la planche : le node 4:1442 porte
                   `bg-white border border-[#e7e7e7]` et rien d'autre. */
                  /* Marges relevees : icone et legende a x=19, y=19 (4:1443) -> 20 px
                   uniforme aux deux grands paliers, 15/13 en Mobile. */
                /* 19 et non 20 (14 / 12 en Mobile) : la bordure Figma est
                   interieure et compte dans la marge (icone a 20 du bord, 4:1443) */
                /* Mobile : 27 px sous le score (4:1775 n'est pas en pile : le
                   bas de la carte est a 608, le score finit a 580) */
                padding:["14px 12px 26px", "19px", "19px", "19px"],
                  minWidth:["auto", "320px", "320px", "320px"],
                  /* Obligatoire : le depot n'a aucun reset global de
                     `box-sizing`. Sans lui, 320 + 40 de marge interieure + 2 de
                     filet donnent 362 la ou le node 4:1442 pose 320. */
                  boxSizing:"border-box",
                  display:"flex",
                  flexDirection:"column",
                  /* Contenu cale a gauche a TOUTES les tailles : sur les trois
                     frames, icone, libelle, score et legende partent du meme
                     bord (x=20 en desktop/tablet, x=13 en mobile). */
                  alignItems:"flex-start",
                  /* Repere pour le fond decoratif, et decoupe pour qu'il ne
                     deborde pas du filet de la carte. */
                  position:"relative",
                  overflow:"hidden",
                }}>
                  {/* Node 4:1456 : quadrillage en bas a droite, sous le
                      contenu. Ornemental — il ne doit ni se lire, ni capter un
                      clic, ni gener la selection du pourcentage. */}
                  <Box component="img" src={fondCarteScore} alt="" aria-hidden="true" sx={{
                    /* 172 px aux trois paliers, a 6 px des bords de la carte (5 +
                       le filet) : 4:1456 a 1238 dans 1096..1416, 4:1630, 4:1788 */
                    position:"absolute", right:"5px", bottom:"5px",
                    width:"172px",
                    height:"172px",
                    pointerEvents:"none", userSelect:"none",
                    /* Le fond decoratif « BG » existe sur les TROIS planches (4:1456, 4:1630,
                     4:1788) : il etait masque a xs et sm, or 834 tombe dans sm. */
                  display:"block",
                  }} />
                  {/* ⚠️ CE LIBELLE NE S'AFFICHAIT JAMAIS. Il etait conditionne a
                      `languageValues.formation.votrePositionnement`, une cle qui
                      n'existe NULLE PART dans le dictionnaire : la condition
                      etait donc toujours fausse et la carte se reduisait au
                      pourcentage nu. Defaut muet — aucune erreur, aucun test
                      rouge, juste un morceau de planche absent. Trouve le 18/09
                      en comparant la capture LAB au node 4:1442, qui porte bien
                      « Score global » en 18 px a droite de l'icone.
                      Le libelle vit maintenant sous sa propre cle, `scoreGlobal`,
                      qui est definie. */}
                  <Box sx={{ display:"flex", alignItems:"center", gap:["12px", "10px", "10px", "10px"] /* 4:1787 a 12 de l'icone en Mobile, 4:1454 a 10 */, alignSelf:"stretch", marginBottom:["28px", "27px", "27px", "27px"] /* jusqu'au haut des chiffres, rognes : 4:1455 a 292 sous l'icone finie a 265 ; 4:1790 a 546 sous 518 */ }}>
                    {/* Boite d'icone du node 4:1443 : 50 px, fond creme, filet
                        clair, et a l'interieur un carre blanc de 40 px porte par
                        une ombre douce. */}
                    <Box sx={{
                      flexShrink:0, width:["45px", "50px", "50px", "50px"], height:["45px", "50px", "50px", "50px"],   /* 45 en Mobile (4:1776) */
                      backgroundColor:"#FAF8F6", border:"0.5px solid #E6E6E7",
                      display:"flex", alignItems:"center", justifyContent:"center",
                    }}>
                      <Box sx={{
                        width:["35px", "40px", "40px", "40px"], height:["35px", "40px", "40px", "40px"], backgroundColor:"white",   /* 35 en Mobile (4:1777) */
                        boxShadow:"0px 2px 1.5px 0.5px rgba(149,160,178,0.16)",
                        display:"flex", alignItems:"center", justifyContent:"center",
                      }}>
                        <Box component="img" src={iconeScoreGlobal} alt=""
                             sx={{ width:"21.58px", height:"22px", display:"block" }} />
                      </Box>
                    </Box>
                    <Box sx={{ color:"rgba(26,25,37,0.75)", fontSize:["16px", "18px", "18px", "18px"], letterSpacing:"-0.02em" /* 4:1454, 4:1787 */ }}>
                      {languageValues?.formation?.scoreGlobal?.[actLanguage]}
                    </Box>
                  </Box>
                  {/* FIGMA EXACT, et les trois frames ne disent PAS la meme chose.
                      Desktop `4:1455`/`4:1458` et Tablet `4:1629`/`4:1632` :
                      la legende est SOUS le nombre, calee a gauche (x=20).
                      Mobile `4:1790`/`4:1792` : elle passe A DROITE du nombre
                      (nombre x=13, legende x=130), sur la meme ligne — la
                      carte mobile est basse (150 px) et large (350), il n'y a
                      pas la hauteur pour empiler.
                      Ce n'est donc pas une adaptation deduite : c'est la
                      frame mobile qui l'impose. */}
                  <Box sx={{
                    display:"flex",
                    flexDirection:["row", "column", "column", "column"],
                    alignItems:["flex-end", "flex-start", "flex-start", "flex-start"],
                    gap:["60px", "25px", "25px", "25px"],   /* nombre / legende de mediane */
                    width:"100%",
                  }}>
                    {/* 62 et 32 px (52 et 26 en Mobile), encre #1A1925, le « % » a 75 %,
                        interlettrage -2 % ; rognes, comme la planche (4:1455, 4:1457,
                        4:1790, 4:1791) */}
                    {/* le nombre et le « % » sont deux textes cote a cote, comme
                        sur la planche (4:1455 et 4:1457), alignes sur leur ligne
                        de base : le « % » dans le nombre donnait un texte de
                        deux lignes de tailles differentes */}
                    <Box sx={{ display:"flex", alignItems:"baseline", flexShrink:0, userSelect:"none" }}>
                      <Box sx={{ color:"#1A1925", fontSize:["52px", "62px", "62px", "62px"], lineHeight:"36px", letterSpacing:"-0.02em", textBox:"trim-both cap alphabetic" }}>
                        {Number.isFinite(results?.master?.score) ? Math.round(results.master.score) : 0 /* jamais « NaN » (07/10/2026) */}
                      </Box>
                      <Box sx={{ fontSize:["26px", "32px", "32px", "32px"], lineHeight:"36px", color:"rgba(26,25,37,0.75)", letterSpacing:"-0.02em", textBox:"trim-both cap alphabetic",
                        /* la planche pose le « % » 5 px DANS la boite du nombre
                           (4:1457 a 1178, le nombre finit a 1183) ; en francais,
                           l'espace fine du point 1.5 (review du 5 oct) le separe
                           du nombre, ecart voulu par Nawal sur la planche 14 */
                        marginLeft: actLanguage === 'en' ? [0, "-3.7px", "-3.7px", "-3.7px"] : 0 }}>{separateurPourcent(actLanguage)}%</Box>
                    </Box>
                    {libelleMediane('master') &&
                      <Box sx={{
                        /* 14 / 19,6 (13 / 18,2 en Mobile), encre a 60 % : 4:1458, 4:1792 */
                        fontSize:["13px", "14px", "14px", "14px"],
                        color:"rgba(10,10,11,0.6)",
                        lineHeight:1.4,
                        textAlign:["right", "left", "left", "left"],
                        /* Mobile : la mediane prend la place a droite du score et
                           finit a 20 px du bord de la carte (4:1792 : 152..352
                           dans 22..372) */
                        flex:["1 1 auto", "none", "none", "none"],
                        marginRight:["7px", 0, 0, 0],
                      }}>
                        {libelleMediane('master')}
                      </Box>
                    }
                  </Box>
                </Box>
              }
              </Box>
              {/* ⛔ POINT 1 DE LA REVUE APPRENANT (Nawal, 04/09) — GRILLE DE CARTES.
                  L'ecran s'appelle « Diagnostic » et ne livrait qu'une liste
                  verticale de lignes reduites a un pourcentage encadre et un nom :
                  ni grille, ni code couleur, et une liste ferree a gauche alors
                  que le titre au-dessus est centre.
                  Le Figma prevoit une grille de cartes, chacune portant trois
                  choses : le pourcentage precede d'une barre verticale coloree
                  selon le niveau, le nom de la competence, et un texte
                  d'interpretation.
                  ⚠️ Le texte d'interpretation EXISTE deja dans le modele de
                  donnees (`seuils[].text`, rendu plus bas) : il est simplement
                  vide dans les contenus. Comme pour le bloc d'explication du
                  quiz, ce n'est pas a developper, c'est a rediger.
                  Point 16 : la grille defile a l'interieur de son cadre plutot
                  que de pousser le bandeau, avec une barre visible. */}
              {/* Le fondu est pose sur un conteneur qui NE defile PAS : dans le
                  bloc defilant il glisserait avec le contenu et ne marquerait
                  plus le bord. */}
              <Box sx={{
                /* ⚠️ FILET D'OUVERTURE DE LA LISTE, absent du code. Le node
                   « Line 446 » ouvre ce bloc sur les TROIS planches : 1320 de
                   large en Desktop (4:1460), 750 en Tablet (4:1634), 350 en
                   Mobile (4:1794). Il separe la carte de score de la liste des
                   competences ; sans lui les deux blocs se touchent. */
                /* ⛔ 04/10/2026 : le filet est un ELEMENT (`hr`, ci-dessous), plus
                   une bordure de ce bloc, et les ecarts sont ceux de la planche :
                   60 px entre l'en-tete et le filet (40 en Mobile : 4:1432,
                   4:1765), 40 entre le filet et le titre de la liste (30 en
                   Mobile : 4:1459, 4:1793). Le code posait 50 et 40. */
                position:"relative",
                display:"flex",
                flexDirection:"column",
                flex:1,
                minHeight:0,
                width:"100%",
                marginTop: afficherCategories ? ["40px", "60px", "60px", "60px"] : 0,
              }}>
              {afficherCategories &&
                <Box component="hr" sx={{ border:0, margin:0, height:"1px", flexShrink:0, width:"100%", backgroundColor:"#E7E7E7", marginBottom:["30px", "40px", "40px", "40px"] }} />}
              {/* TITRE DE LA LISTE — planche 14 : « Vos six compétences, du
                  plus au moins solide: ». Il manquait entierement : la liste
                  arrivait sans etre annoncee, et rien ne disait qu'elle est
                  TRIEE. Or elle l'est — `categorieByScore.sort((a,b) => b.score
                  - a.score)` plus haut — donc la phrase informe vraiment, elle
                  ne decore pas.
                  Il ne s'affiche que s'il y a des competences : sur un contenu
                  qui n'en definit aucune, annoncer une liste vide serait pire
                  que se taire. */}
              {/* Point 10 : meme regle que le titre ci-dessus. `{n}` reste
                  remplace, y compris dans un texte saisi par Nawal : c'est le
                  nombre de competences reellement affichees, elle n'a aucun moyen
                  de le connaitre a la saisie. */}
              {afficherCategories && !!titreEditable("titreCompetences", t(languageValues?.formation?.vosCompetences)) &&
                <Box sx={{
                  /* noir, 20 / 24 (16 / 19,2 en Mobile), rogne : 4:1462, 4:1796 */
                  color:"text.black",
                  fontSize:["16px", "20px", "20px", "20px"],   /* titre de la liste */
                  lineHeight:1.2,
                  textBox:"trim-both cap alphabetic",
                  /* ⚠️ DEUX MARGES S'ADDITIONNAIENT ici : celle du titre et celle
                     de la liste. La planche ne pose qu'un seul ecart. On garde
                     celle de la liste et on annule celle du titre. */
                  marginBottom:"0px",
                  flexShrink:0,
                }}>
                  {titreEditable("titreCompetences", t(languageValues?.formation?.vosCompetences))
                    .replace("{n}", String(competencesAffichees.length))}
                </Box>
              }
              {/* Redesign Valentin 2026. ⛔ 01/10/2026 : citait `8571-6560`,
                 propre au fichier d'ARCHIVE. Noeud « Claude » : `4:1464`, une
                 ligne de competence de 1320 x 131 a x=96 — deja cite ailleurs
                 dans ce fichier, ce qui aurait du mettre la puce a l'oreille.
                 LIGNES de competences
                 (nom a gauche, jauge 20 segments + % a droite), plus la grille
                 de cartes de l'iteration precedente. Le nombre de competences
                 reste dynamique ; la liste defile si elle depasse. */}
              <Box ref={refListe} sx={{
                display:"flex",
                flexDirection:"column",
                gap:"12px",   /* ecart entre lignes : la meme valeur sur les trois planches */
                userSelect:"none",
                position:"relative",
                width:"100%",
                /* l'unique ecart titre de liste / premiere ligne : 30, 20 en Mobile
                   (4:1461, 4:1795) ; sans titre, le filet en porte deja 40 */
                marginTop: (afficherCategories && !!titreEditable("titreCompetences", t(languageValues?.formation?.vosCompetences))) ? ["20px", "30px", "30px", "30px"] : 0,
                marginLeft:"auto",
                marginRight:"auto",
                /* ⛔ 01/10/2026 — PD-13, TRANCHE PAR HICHAM : LA LISTE DEFILE.
                   Le node 4:1459 donne un conteneur de hauteur naturelle, et
                   Nawal avait demande « sans defilement interne » (point 1 du
                   30/09). Ce n'est pas tenable, et c'est mesure : SIX competences
                   font 1381 px de haut sur une planche de 982. Sans fenetre, la
                   liste debordait son parent de 929 px — en silence, par-dessus
                   la cle de lecture des couleurs, que la capture d'Hicham montre
                   coincee entre deux cartes et coupee en deux.
                   Entre deborder sans rien dire et defiler en le disant, Hicham a
                   tranche le 01/10 : on defile. Le fondu ne se montre que s'il
                   reste du contenu dessous (`useDebordeEnBas`), donc une liste
                   courte reste exactement comme avant — la demande de Nawal est
                   tenue dans le seul cas ou elle etait tenable.
                   ⚠️ La cle de lecture reste HORS de cette fenetre : elle doit
                   rester lisible sans defiler. */
                ...BLOC_DEFILANT,
                overflowY:"auto",
                flex:1,
                minHeight:0,
              }}>
                {/* Ecart 8 : le CircularProgress simulait un chargement qui
                    n'existe pas (le score est deja calcule). A la place, un
                    voile centre plein ecran : le R de la marque se compose en
                    pixels, se fige au noir du logo, puis le voile s'efface
                    avec le meme fadeOut qu'avant et revele les resultats.
                    pointerEvents:none : une fois transparent, il ne doit
                    surtout pas intercepter les clics. */}
                {!onlyContent &&
                  <Box sx={{
                    position:"fixed",
                    top:0, left:0, right:0, bottom:0,
                    display:"flex",
                    flexDirection:"column",
                    alignItems:"center",
                    justifyContent:"center",
                    gap:"26px",
                    background:"#FAF8F7F2",
                    zIndex:5,
                    pointerEvents:"none",
                    animation : `${MISE_EN_SCENE_BILAN.dureeVoile}s linear ${MISE_EN_SCENE_BILAN.debutVoile}s fadeOut`,
                    animationFillMode: "both !important",
                  }}>
                    <ChargementPixelR/>
                    <Box sx={{fontSize:["15px","16px"], color:"text.black", userSelect:"none"}}>
                      {t(languageValues?.formation?.analyseEnCours)}
                    </Box>
                  </Box>
                }
                {afficherCategories && competencesAffichees.map((cat, i) => {
                  let subTitle = ""
                  let iSeuil = 0
                  for (let i=0; i<cat?.seuils?.length; i++) {
                    let seuil = cat?.seuils[i]
                    if (cat?.score >= seuil?.seuil) {
                      subTitle = seuil?.text || ""
                      iSeuil = i
                      break;
                    }
                  }

                  let dureeAnim = MISE_EN_SCENE_BILAN.dureeCarte //secondes
                  let delayAnim = (i+1)*MISE_EN_SCENE_BILAN.delaiCarte //delay anim en secondes
                  // `totalAnim` a disparu avec les quatre bordures animees
                  // qu'elle cadencait, remplacees par la barre de niveau.
                  /* Jauge : segments remplis = fraction du score sur 20, colores
                     selon le palier ; le reste en gris clair. */
                  const remplis = Math.max(0, Math.min(20, Math.round(((cat?.score ?? 0) / 100) * 20)))
                  const couleur = couleurDuPalier(iSeuil, cat?.seuils?.length)
                  return (
                    <Box key={i} sx={{
                      width:"100%",
                      display:"flex",
                      /* 04/10/2026 : la jauge passe SOUS le texte en Tablet aussi
                         (4:1638 : colonne, ecart 20), a cote seulement aux grands
                         paliers (4:1464 : rangee, ecart 40, centree). */
                      flexDirection:["column", "column", "row", "row"],
                      alignItems:["stretch", "stretch", "center", "center"],
                      justifyContent:"space-between",
                      gap:["20px", "20px", "40px", "40px"],
                      /* ⚠️ LA LIGNE EST UNE CARTE, PAS UNE LIGNE DE TABLEAU.
                         Node 4:1464 : `bg-white`, bordure 1 px #E7E7E7 sur les
                         QUATRE cotes, 30 px de marge interieure, aucun rayon,
                         aucune ombre. Le code ne posait qu'un filet sous la
                         ligne et aucun fond : la liste se lisait comme un
                         tableau alors que la planche pose des cartes.
                         Recomposition : 30 + 71 + 30 = 131, la hauteur du node. */
                      backgroundColor:"#FFFFFF",
                      border:"1px solid #E7E7E7",
                      /* 24 / 29 et non 25 / 30 : la bordure Figma est INTERIEURE et
                         compte dans la marge (4:1798 : 25 ; 4:1464 : 30), la bordure
                         CSS s'y ajoute */
                      padding:["24px", "29px", "29px", "29px"],
                      /* ⚠️ OBLIGATOIRE ICI. L'application n'a AUCUN reset global
                         de `box-sizing` (pas de CssBaseline, pas de regle `*`) :
                         chaque endroit qui pose `width:100%` AVEC du remplissage
                         doit le declarer lui-meme. Sans cette ligne, la carte
                         mesure 100 % + 50 px + 2 px et sort de la fenetre de
                         52 px : en mobile, la bordure droite disparait et le
                         score est rogne. Trouve par le filet, jamais vu avant
                         parce que cet ecran n'avait jamais ete rendu. */
                      boxSizing:"border-box",
                      animation : !onlyContent&&`${dureeAnim}s linear ${delayAnim}s fadeIn`,
                      animationFillMode: "both !important",
                      opacity : onlyContent?1:0,
                    }}>
                      {/* Gauche : nom de la competence + texte d'interpretation. */}
                      {/* colonne de 650 px (300 en Mobile, toute la largeur) : 4:1465, 4:1639 */}
                      <Box sx={{ flex:["0 0 auto", "0 0 auto", "0 1 650px", "0 1 650px"], maxWidth:["100%", "650px", "650px", "650px"], minWidth:0, textAlign:"left" }}>
                        <EditableField
                          field={cat?.title ? cat.title : cat?.key}
                          isEditor={false}
                          sx={{
                            /* 22 px (20 en Mobile), encre #1A1925, -2 %, rogne :
                               12 px jusqu'a la phrase se comptent entre textes rognes
                               (4:1466 a 590, phrase a 617 ; 4:1800) */
                            color:"text.ink",
                            fontSize:["20px", "22px", "22px", "22px"],   /* nom de competence */
                            lineHeight:"36px",
                            letterSpacing:"-0.02em",
                            textBox:"trim-both cap alphabetic",
                            /* La casse vient du contenu ; on ne force que la
                               premiere lettre pour les anciennes categories en
                               minuscules (POINT 61, fiche V2). */
                            "&::first-letter": { textTransform:"uppercase" },
                          }}
                        />
                        {/* Point 3.1 : toujours la en editeur (vide compris), seulement
                            s'il y a un texte cote apprenant, comme avant */}
                        {(subTitle || (isEditor && cat?.seuils?.[iSeuil])) &&
                          <EditableField
                            field={subTitle}
                            isEditor={isEditor}
                            sx={{
                              /* 16 / 22,4 (14 / 19,6 en Mobile), encre a 70 % : 4:1467, 4:1801 */
                              color:"rgba(10,10,11,0.7)",
                              fontSize:["14px", "16px", "16px", "16px"],   /* texte d'interpretation */
                              lineHeight:"140%",
                              marginTop:"12px",
                              ...champDePalierVide(cat?.seuils?.[iSeuil]?.seuil),
                            }}
                            type={["categorie", cat?.key, "seuils", iSeuil, "text"]}
                          />
                        }
                      </Box>
                      {/* Droite : jauge 20 segments + pourcentage. */}
                      {/* En Tablet, la rangee jauge + pourcentage garde sa largeur, ferree
                          a gauche sous le texte (4:1643 : 418 px a x=72) ; en Mobile elle
                          est etiree (B86, voir la jauge). */}
                      <Box sx={{ display:"flex", alignItems:"center", gap:["20px", "15px", "15px", "15px"],   /* echelle / pourcentage */ flexShrink:0, alignSelf:["stretch", "flex-start", "auto", "auto"] }}>
                        {/* Gouttiere de 8 px, relevee DEUX fois : `gap-[8px]` sur le
                            node 4:1470, et par recomposition du pas de 18 px entre
                            segments (18 - 10 de largeur). */}
                        {/* ⛔ 04/10/2026 — B86 : EN MOBILE, LA JAUGE PREND LA PLACE QUI
                            RESTE, ELLE NE LA FIXE PAS. 20 x 8 + 19 x 5 = 255 px fixes,
                            plus 20 d'ecart et le pourcentage : 330 px dans une carte
                            qui en offre 297 a 393 px de large. Le pourcentage
                            debordait de 26 px dans la marge de la carte, et etait
                            COUPE a 375 et 320 px (iPhone SE, mini). La planche Mobile
                            remplit la carte au pixel : jauge 237 + 20 + « 87% » 43 =
                            les 300 de la carte (4:1803, 4:1804, 4:1823). Les segments
                            gardent 8 px tant qu'il y a la place, puis retrecissent,
                            et l'ecart avec eux. Tablet et Desktop : inchanges.
                            ⚠️ Les segments n'ont aucune largeur propre : la jauge ne
                            grandit que parce que ce bloc est ETIRE sur la largeur de la
                            carte (carte en colonne, `stretch`, au palier xs). Si la
                            carte repasse en rangee en Mobile, la jauge tombe a 0 px
                            sans erreur (relecture du 04/10). */}
                        <Box sx={{ display:"flex", gap:["clamp(2px, 1.25vw, 5px)", "8px", "8px", "8px"], flex:["1 1 auto", "none", "none", "none"], minWidth:0, maxWidth:["255px", "none", "none", "none"] }}>
                          {[...Array(20)].map((_, s) => (
                            <Box key={s} sx={{
                              width:["auto", "10px", "10px", "10px"],   /* 10 px sur 8 occurrences Desktop et Tablet, 8 en Mobile (au plus : voir B86) */
                              /* filet interieur #EFEFEF sur les segments VIDES, de la
                                 couleur de leur fond (4:1490, 4:1664, 4:1822) ; celui
                                 des segments pleins est masque dans la planche (4:1471) */
                              border: s < remplis ? "none" : "1px solid #EFEFEF",
                              boxSizing:"border-box",
                              flex:["1 1 0", "none", "none", "none"],
                              maxWidth:["8px", "none", "none", "none"],
                              height:["30px", "40px", "40px", "40px"],   /* 40 px Desktop et Tablet, 30 en Mobile */
                              background: s < remplis ? couleur : "#EFEFEF",   /* #EFEFEF et non #E7E7E7 (nodes 4:1489, 4:1490) */
                            }} />
                          ))}
                        </Box>
                        {/* ⛔ PAS DE LEGENDE DE COHORTE ICI, ET C'EST VERIFIE
                            DANS FIGMA, PAS DEDUIT. Le node 4:1492 porte
                            « Nettement au-dessus de la cohorte » : ce n'est
                            PAS une mediane, c'est un JUGEMENT RELATIF, meme
                            famille que « Vous etes dans le tiers superieur ».
                            Hicham a explicitement separe les deux le 19/09 :
                            la mediane est un repere, le classement demande un
                            arbitrage pedagogique de Nawal. Tant qu'il n'a pas
                            eu lieu, cette ligne n'existe pas.
                            J'avais commence par y mettre une mediane par
                            competence — la donnee le permettait, le calcul
                            existe et il est teste. Mais ce n'est pas ce que la
                            planche demande, et « la donnee existe » n'est pas
                            une raison d'afficher. */}
                        <Box sx={{ color:"#0A0A0B", fontSize:["22px", "26px", "26px", "26px"],   /* pourcentage de ligne */ lineHeight:1.4, letterSpacing:"-0.005em", textBox:"trim-both cap alphabetic" /* 4:1491, 4:1823 */, minWidth:["48px", "58px", "62px", "62px"], textAlign:"right" }}>
                          {pourcentLisible(cat?.score, actLanguage)}
                        </Box>
                      </Box>
                    </Box>
                  )
                })}
              </Box>
              {/* ⛔ 30/09/2026 — FICHE DE RETOUR DE NAWAL, POINT 1 (et POINT 87,
                  le meme defaut dans l'apercu admin : c'est le meme composant,
                  au meme endroit). Un `FonduBas` etait pose ici, et il passait
                  par-dessus la derniere competence (« Planification »).
                  POURQUOI IL EXISTAIT : `FonduBas` sert a prevenir qu'un bloc
                  DEFILE, parce que macOS masque la barre de defilement au repos
                  (FormatsHelpers l. 510-519, demande de la vague 1).
                  POURQUOI IL N'AVAIT RIEN A FAIRE ICI : le conteneur de la liste
                  des competences n'a AUCUNE fenetre interne — c'est ecrit vingt
                  lignes plus haut et releve sur le node 4:1459 (hauteur
                  naturelle, 1320 x 643). Il ne defile jamais. Le fondu ne
                  signalait donc rien : il ne faisait que couvrir le bas de la
                  liste. Retire, sans contrepartie.
                  Nawal l'avait prevu : « si c'est un effet de zone de
                  defilement, afficher les competences dans la page, sans
                  defilement interne ». Elles y sont deja.
                  ⚠️ `FonduBas` reste employe par `FormatQuizz` (la bande basse),
                  dont le comportement de defilement n'est pas le meme : ce lot
                  n'y touche pas. */}
              {/* ⛔ 01/10/2026 — IL REVIENT, MAIS SOUS CONDITION, ET CE N'EST PAS
                  UN RETOUR EN ARRIERE. Le 30/09 il etait retire parce qu'il se
                  posait sur un bloc qui NE DEFILAIT PAS : il masquait la derniere
                  competence pour n'annoncer rien, et Nawal avait raison de le
                  refuser. Depuis PD-13, la liste defile vraiment, et le fondu ne
                  s'affiche que s'il reste du contenu dessous. Une liste courte
                  n'en a donc toujours aucun — l'ecran de sa capture est inchange.
                  Il est pose ICI, hors de la fenetre qui defile : sinon il
                  defilerait avec le contenu au lieu de marquer le bord. */}
              {afficherCategories && listeDeborde && <FonduBas />}
              </Box>
              {/* CLE DE LECTURE DES COULEURS. Proposee le 07/09 hors fiche,
                  VALIDEE le meme jour (fiche V2, point 1.6) : « la couleur ne
                  doit jamais porter l'information seule », RdYlGn n'etant pas
                  lisible par les daltoniens rouge-vert. C'est le mot qui fait
                  foi, la couleur ne fait que renforcer.
                  Les trois libelles sont ceux arretes par Nawal, en dur pour
                  trois bandes. Au-dela, elle a dit qu'elle fournirait les mots
                  le moment venu : on affiche alors le seuil lui-meme (« ≥ 40 % »)
                  plutot qu'un mot invente, et la legende reste juste. */}
              {/* ⛔ Trouve sur le vrai catalogue le 07/09 : beaucoup de contenus
                  n'ont qu'UN SEUL palier par competence. La legende affichait
                  alors « ≥ 0 % », une ligne qui n'apprend rien. Une seule bande
                  n'a pas besoin de cle de lecture : on ne l'affiche plus. */}
              {afficherCategories && (competencesAffichees[0]?.seuils?.length || 0) > 1 &&
                <Box sx={{
                  marginTop:["16px", "20px", "22px", "24px"],
                  display:"flex",
                  /* ferree a gauche comme tout l'ecran depuis le 04/10 */
                  justifyContent:"flex-start",
                  flexWrap:"wrap",
                  gap:["12px", "18px", "20px", "24px"],
                  fontSize:["12px", "13px", "13px", "14px"],
                  color:"text.subTitle",
                  userSelect:"none",
                }}>
                  {(() => {
                    /* Les bandes de la legende sont celles du contenu, pas un
                       nombre fige : on prend les seuils de la premiere
                       competence affichee. */
                    const paliers = competencesAffichees[0]?.seuils || []
                    const n = paliers.length || 3
                    const mots = ["niveauAcquis", "niveauEnCours", "niveauATravailler"]
                    return paliers.map((palier, i) => (
                      <Box key={i} sx={{display:"flex", alignItems:"center", gap:"6px"}}>
                        <Box sx={{
                          width:"4px", height:"14px", flexShrink:0,
                          background: couleurDuPalier(i, n),
                          /* Au milieu de l'echelle a trois classes, RdYlGn est
                             un jaune tres pale (#FFFFBF) qui disparaitrait sur
                             le fond creme de la page : un filet le detoure. */
                          border:"1px solid rgba(10,10,11,0.18)",
                          boxSizing:"border-box",
                        }} />
                        <Box>{n === 3
                          ? languageValues?.formation?.[mots[i]]?.[actLanguage]
                          : "≥ " + pourcentLisible(palier?.seuil, actLanguage)}</Box>
                      </Box>
                    ))
                  })()}
                </Box>
              }
            </Box>
          </Box>
        </Box>
        }
        {(onlyContent && results === null && isEditor !== true) &&
          <Box sx={{
            minWidth:["150px", "200px"],
            minHeight:["150px", "200px"],
            marginTop:"50px"
          }}>
            <CircularProgress style={{color:"black", width:"100%", height:"100%"}}/>
          </Box>
        }
        </BlocIllustre>
        )}
      </CenterContainer>
      <BottomBar
        actFormation={actFormation}
        actIndex={actIndex}
        onlyContent={isEditor ? false : onlyContent}
        isEditor={isEditor}
        blanche
        buttons={
          <EditableButton
            isEditor={false}
            /* PD-10 (25/09/2026) : pour le candidat, aucune animation a attendre,
               donc « Suivant » actif tout de suite. On garde seulement la garde
               « resultats pas encore calcules » (le temps du premier effet),
               pour que l'enregistrement parte toujours avec ses resultats ; un
               ecran deja fait n'envoie plus rien, il n'a donc pas a attendre. */
            disabled={vueCandidat
              ? resultatsPasPrets
              : (results === null ? true : !activeButton)}
            type="button"
            /* ⛔ POINT 1.3 DE LA FICHE V2. Le libelle etait celui saisi
               dans le contenu, d'ou les « C'est parti ! » et « On
               continue ? » qui trainaient depuis des annees. Il est
               desormais CALCULE, et la regle ecrase le contenu :
               le bilan n'attend aucune reponse : on passe a la suite.
               Le champ de saisie libre est retire avec lui
               (isEditor={false}) : tant qu'il existait, le libelle
               rederivait au module suivant. */
            field={languageValues?.next?.[actLanguage]}
            sx={{}}
            onClick={() => {
              /* (25/09/2026) La meme garde qu'au-dessus, au cas ou le bouton
                 serait un jour rendu actif trop tot : voir `resultatsPasPrets`. */
              if (resultatsPasPrets) return
              // 27/09/2026 : voir `bilanParLeServeur`, seul isDone part.
              manageFormatResponse(format, bilanParLeServeur
                ? [{ key: 'isDone', value: true }]
                : [{ key: 'isDone', value: true }, { key: 'userChoice', value: results }])
              manageSlide(1)
            }}
          />
        }
      />
    </Box>
  )
}


export default FormatDiag
